import React, { useMemo } from 'react';
import { Form, Tag, Card } from 'antd';
import { get } from 'lodash';
import SectionHeader from './SectionHeader';
import StatRow from './StatRow';
import ModuleSection from './ModuleSection';
import PeriodRangePicker from './PeriodRangePicker';
import {
  formatPeriodLabel,
  getPriorPeriodRange,
} from '../periodUtils';
import s from '../AnnualReviewReport.module.scss';

const CLIENT_TYPE_OPTIONS = ['Career', 'Alumni', 'Central', 'Other'];

const ACCOUNT_HEALTH_COLORS = {
  green: 'green',
  yellow: 'gold',
  red: 'red',
};

const getModuleConfig = (periodLabel, priorPeriodLabel) => [
  {
    key: 'usersAndSignups',
    title: 'Users & Signups',
    showEnabledTag: false,
    stats: [
      {
        key: 'totalSignedUpStudents',
        label: 'Total signed-up Students (Student + Grad)',
        tooltip:
          'All students and graduate students who have completed signup on the platform since launch.',
      },
      {
        key: 'totalSignedUpAlumni',
        label: 'Total signed-up Alumni',
        tooltip:
          'All alumni who have completed signup on the platform since launch.',
      },
      {
        key: 'studentSignupGrowthPct',
        label: `% growth — Student signups ${periodLabel} vs ${priorPeriodLabel}`,
        tooltip: `Period-over-period change in completed student signups, comparing ${periodLabel} vs ${priorPeriodLabel}.`,
      },
      {
        key: 'alumniSignupGrowthPct',
        label: `% growth — Alumni signups ${periodLabel} vs ${priorPeriodLabel}`,
        tooltip: `Period-over-period change in completed alumni signups, comparing ${periodLabel} vs ${priorPeriodLabel}.`,
      },
      {
        key: 'usersNotCompletedSignup',
        label: 'Users not completed signup (Opportunity)',
        tooltip:
          'Total imported user records (student + alumni) who have been invited but have not yet completed signup.',
      },
    ],
  },
  {
    key: 'connections',
    title: 'Connections',
    showEnabledTag: false,
    stats: [
      {
        key: 'totalConnectionsMade',
        label: `Total connections made in ${periodLabel}`,
        tooltip: `Total two-way connections made in ${periodLabel}, including long-term mentorship connections. A connection is counted when the recipient responds or accepts a meeting request.`,
      },
      {
        key: 'connectionRatePct',
        label: 'Connection rate %',
        tooltip:
          'Percentage of all signed-up users who have at least one two-way connection, across all connection types and sources including programs.',
        format: 'percentage',
      },
      {
        key: 'responseRatePct',
        label: 'Response rate %',
        tooltip: `Percentage of message threads started in ${periodLabel} that received at least one response. Includes replies, meeting acceptances, and rejections.`,
        format: 'percentage',
      },
      {
        key: 'totalMessagesSent',
        label: 'Total messages sent',
        tooltip: `Total individual messages sent between all users in ${periodLabel}, across all connection types.`,
      },
      {
        key: 'totalMeetingsHappened',
        label: 'Total meetings happened',
        tooltip:
          'Total meetings that happened on the platform. Excludes rejected and no-show meetings.',
      },
    ],
  },
  {
    key: 'programs',
    title: 'Programs',
    isPrograms: true,
    showEnabledTag: true,
    stats: [
      {
        key: 'programsStarted',
        label: 'Programs started',
        tooltip: `Number of formal programs with any registration that happened in ${periodLabel}`,
      },
      {
        key: 'totalProgramRegistrations',
        label: 'Total program registrations',
        tooltip: `Total user registrations in any hub program in ${periodLabel}`,
      },
      { key: 'menteeRegistrations', label: 'Mentee registrations' },
      { key: 'mentorRegistrations', label: 'Mentor registrations' },
      {
        key: 'longTermMentorshipsStarted',
        label: 'Long-term mentorships started',
        tooltip: `Long-term mentorship pairings that began in ${periodLabel} across all formal programs.`,
      },
      {
        key: 'longTermMentorshipsCompleted',
        label: 'Long-term mentorships completed',
        tooltip: `Long-term mentorship pairings that were marked complete in ${periodLabel} across all formal programs.`,
      },
      {
        key: 'avgStarRating',
        label: 'Avg star rating',
        tooltip: `Average star rating from post-mentorship feedback surveys submitted in ${periodLabel}. Rated on a 1–5 scale by both mentees and mentors.`,
      },
      {
        key: 'meaningfulInteractionsPct',
        label: 'Meaningful interactions',
        tooltip: `Percentage of respondents who agreed or strongly agreed that their interaction was meaningful, from surveys submitted in ${periodLabel}.`,
        format: 'percentage',
      },
    ],
  },
];

const AnnualReviewReportContent = ({
  data,
  clientType,
  dateRange,
  onClientTypeChange,
  onPeriodChange,
}) => {
  const periodLabel =
    formatPeriodLabel(dateRange) || get(data, 'period.label');
  const priorPeriodLabel =
    formatPeriodLabel(getPriorPeriodRange(dateRange)) ||
    get(data, 'period.priorLabel');

  const moduleConfig = useMemo(
    () => getModuleConfig(periodLabel, priorPeriodLabel),
    [periodLabel, priorPeriodLabel]
  );

  const accountSnapshot = get(data, 'accountSnapshot') ?? {};
  const hubId = get(data, 'hubId');
  const hubName = get(data, 'hubName');
  const hubCreatedAt = get(data, 'hubCreatedAt');
  const vitallySynced = get(data, 'vitallySynced', false);

  const handlePeriodChange = nextRange => {
    if (!nextRange?.start || !nextRange?.end) return;
    if (onPeriodChange) {
      onPeriodChange(nextRange);
    }
  };

  return (
    <div className={s.content}>
      <div className={s.header}>
        <h2 className={s.mainTitle}>Annual Review Report</h2>
        <div className={s.headerMeta}>
          <span>Hub ID: {hubId ?? '—'}</span>
          <span className={s.hubName}>{hubName ?? '—'}</span>
          {vitallySynced && (
            <Tag color="blue" aria-label="Vitally synced">
              Vitally Synced
            </Tag>
          )}
        </div>
      </div>

      <Card className={s.sectionCard}>
        <SectionHeader title="Account Snapshot" />
        <Form layout="vertical" className={s.accountForm}>
          <div className={s.formRow}>
            <StatRow
              label="Client Name"
              value={accountSnapshot.clientName}
              id="client-name"
            />
            <StatRow label="CSM" value={accountSnapshot.csm} id="csm" />
          </div>
          <div className={s.formRow}>
            <StatRow label="ARR" value={accountSnapshot.arr} id="arr" />
            <StatRow
              label="Renewal Date"
              value={accountSnapshot.renewalDate}
              id="renewal-date"
            />
          </div>
          <div className={s.formRow}>
            <StatRow label="NPS" value={accountSnapshot.nps} id="nps" />
            <Form.Item
              label="Account Health"
              colon={false}
              className={s.statRow}
            >
              <Tag
                className={s.tagFieldInline}
                color={
                  ACCOUNT_HEALTH_COLORS[accountSnapshot.accountHealth] ||
                  'default'
                }
                aria-label={`Account health: ${accountSnapshot.accountHealth ?? 'Not set'}`}
              >
                {accountSnapshot.accountHealth ?? '—'}
              </Tag>
            </Form.Item>
          </div>
          <Form.Item label="Client Type" colon={false} className={s.statRow}>
            <div className={s.clientTypeTagsDiv}>
              {CLIENT_TYPE_OPTIONS.map(opt => (
                <Tag.CheckableTag
                  key={opt}
                  className={s.clientTypeTags}
                  checked={(clientType ?? accountSnapshot.clientType) === opt}
                  onChange={() => onClientTypeChange(opt)}
                  aria-label={`Client type: ${opt}`}
                >
                  {opt}
                </Tag.CheckableTag>
              ))}
            </div>
          </Form.Item>
        </Form>
      </Card>

      <Card className={s.sectionCard}>
        <div className={s.platformDataHeader}>
          <SectionHeader title={`Platform Data — ${periodLabel}`} />
          <PeriodRangePicker
            value={dateRange}
            onChange={handlePeriodChange}
            hubCreatedAt={hubCreatedAt}
          />
        </div>
        {moduleConfig.map(config => {
          const moduleData = get(data, config.key, {});
          return (
            <ModuleSection
              key={config.key}
              title={config.title}
              enabled={get(moduleData, 'enabled', true)}
              moduleData={moduleData}
              statConfigs={config.stats}
              isPrograms={config.isPrograms === true}
              reviewYear={periodLabel}
              usageHealth={get(moduleData, 'usageHealth')}
              showEnabledTag={config.showEnabledTag}
            />
          );
        })}
      </Card>
    </div>
  );
};

export default AnnualReviewReportContent;
